import dayjs, { Dayjs } from 'dayjs'
import {
  ClockinDetail,
  Nullable,
  StaffMemberDetail,
  StaffMemberPayRule,
  StaffMemberPaytype,
  StaffMemberRemunerationHistory,
  StaffMemberRole,
  transformMemberRolesOnSave,
  transformStaffMemberRemunerationHistoryOnSave,
} from '$models'
import { logger } from '$utils'
import { DEFAULT_GUID } from 'src/components/routes/constants'
import {
  RemunerationType,
  EmploymentType,
  Region,
  PayrollSystem,
  Country,
} from '$constants'
import { getDateStringWithoutTimeZone } from 'src/components/routes/_common/helpers'
import {
  StaffMemberRemunerationSnapshot,
  sortRemunerationSnapshot,
  transformRemunerationSnapshotOnGet,
} from 'src/models/interfaces/remunerationSnapshot'
import sortBy from 'lodash/sortBy'
import {
  StaffMemberNz,
  transformStaffMemberNzOnGet,
  transformStaffMemberNzOnSave,
} from './staffMemberNz'
import {
  StaffMemberAu,
  transformStaffMemberAuOnGet,
  transformStaffMemberAuOnSave,
} from './staffMemberAu'
import {
  StaffMemberXeroAu,
  transformStaffMemberXeroAuOnGet,
  transformStaffMemberXeroAuOnSave,
} from './staffMemberXeroAu'
import { Contact } from '../contact'
import { OnboardingDetails } from '../onboardingDetails'

export type StaffMember = StaffMemberNz | StaffMemberAu | StaffMemberXeroAu

export interface BaseStaffMember {
  discriminator?: Country
  id?: string
  posIdentifier?: string
  posStaffName?: string
  datestampStarted: Nullable<Dayjs>
  datestampModified?: Dayjs
  datestampDeleted?: Dayjs
  firstName: Nullable<string>
  lastName: Nullable<string>
  preferredName?: string
  middleName?: string
  dob: Nullable<Dayjs>
  gender?: string
  phoneHome?: string
  phoneMobile?: string
  email: Nullable<string>
  homeAddressUnit?: string
  homeAddressStreet?: string
  homeAddressSuburb?: string
  homeAddressCity?: string
  homeAddressState?: Region
  homeAddressZipCode?: string
  homeAddressCountry?: string
  payCode?: string
  showInRoster: boolean
  defaultRoleId: Nullable<string>
  lastClock?: ClockinDetail
  activeRemuneration?: StaffMemberRemunerationHistory
  memberRoles: StaffMemberRole[]
  paytypes: StaffMemberPaytype[]
  rules: StaffMemberPayRule[]
  thirdPartyEmployeeId?: string | null
  remunerationType?: RemunerationType
  reportsTo?: string
  lineManagerId?: string
  employmentType: Nullable<EmploymentType>
  weeklyContractedHours?: number
  applyAdjustments: boolean
  bankAccountName?: string
  bankAccountNumber?: Nullable<string>
  bsb?: Nullable<string>
  remunerationSnapshots: StaffMemberRemunerationSnapshot[]
  emergencyContact?: Contact
  onboardingDetails?: OnboardingDetails
}

export const newStaffMember: StaffMember = {
  showInRoster: true,
  datestampStarted: dayjs().startOf('day'),
  memberRoles: [],

  //If I don't add this in the request then it fails to add a new staff member
  activeRemuneration: {
    id: DEFAULT_GUID,
    type: 0,
    rate: 0,
    validFrom: dayjs('0001-01-01T00:00:00+00:00'),
  },

  paytypes: [],
  rules: [],
  remunerationSnapshots: [],
  defaultRoleId: null,
  firstName: null,
  lastName: null,
  email: null,
  dob: null,
  employmentType: null,
  applyAdjustments: true,
}

export function mapStaffMemberToStaffMemberDetail(
  staffMember: StaffMember
): StaffMemberDetail | null {
  try {
    const defaultMemberRole: StaffMemberRole | undefined =
      staffMember.memberRoles.find(
        (memberRole) => memberRole.roleId === staffMember.defaultRoleId
      )
    return {
      id: staffMember.id,
      datestampDeleted: staffMember.datestampDeleted,
      name: `${staffMember.firstName} ${
        staffMember.lastName ? staffMember.lastName : ''
      }`,
      posStaffName: staffMember.posStaffName,
      defaultMemberRoleRoleId: defaultMemberRole?.roleId,
      defaultMemberRoleRoleName: defaultMemberRole?.roleName,
      defaultMemberRoleHourlyRate: defaultMemberRole?.hourlyRate,
      memberRoles: [...staffMember.memberRoles],
      lastClockClockinTime: staffMember.lastClock?.clockinTime
        ? dayjs(staffMember.lastClock?.clockinTime)
        : null,
      lastClockClockoutTime: staffMember.lastClock?.clockoutTime
        ? dayjs(staffMember.lastClock?.clockoutTime)
        : null,
      lastClockId: staffMember.lastClock?.id,
      lastClockRoleName: staffMember.lastClock?.roleName,
      showInRoster: staffMember.showInRoster,
      activeRemuneration: staffMember.activeRemuneration
        ? { ...staffMember.activeRemuneration }
        : null,
      applyAdjustments: staffMember.applyAdjustments,
    }
  } catch (error) {
    logger.error(
      'Failed to map StaffMember to StaffMemberDetail',
      error,
      staffMember
    )
    return null
  }
}

// Function to apply transformations to StaffMember object on get
export const transformBaseStaffMemberOnGet = (
  staffMember: StaffMember
): StaffMember => {
  return {
    ...staffMember,
    memberRoles: sortBy<StaffMemberRole>(staffMember.memberRoles, 'roleName'),
    dob: staffMember.dob != null ? dayjs.utc(staffMember.dob) : null,
    datestampStarted:
      staffMember.datestampStarted != null
        ? dayjs(staffMember.datestampStarted)
        : null,
    remunerationSnapshots: sortRemunerationSnapshot(
      staffMember.remunerationSnapshots.map(transformRemunerationSnapshotOnGet)
    ),
  }
}

// Function to apply transformations to StaffMember object on save
export const transformBaseStaffMemberOnSave = (
  staffMember: StaffMember
): object => {
  return {
    ...staffMember,
    bankAccountNumber: staffMember.bankAccountNumber?.replaceAll('-', ''),
    dob:
      staffMember.dob != null
        ? getDateStringWithoutTimeZone(staffMember.dob)
        : staffMember.dob,
    memberRoles: staffMember.memberRoles.map(transformMemberRolesOnSave),
    activeRemuneration: transformStaffMemberRemunerationHistoryOnSave(
      staffMember.activeRemuneration
    ),
    emergencyContact:
      !staffMember.emergencyContact?.firstName &&
      !staffMember.emergencyContact?.lastName &&
      !staffMember.emergencyContact?.phoneMobile &&
      !staffMember.emergencyContact?.relationship
        ? null
        : {
            ...staffMember.emergencyContact,
            relationship: !staffMember.emergencyContact?.relationship
              ? null
              : staffMember.emergencyContact?.relationship,
          },
  }
}

// Function to apply transformations to StaffMember object on get
export const transformStaffMemberOnGet = (
  staffMember: StaffMember,
  payrollSystem?: PayrollSystem | null,
  country?: Country
): StaffMember => {
  switch (country) {
    case Country.NewZealand:
      return transformStaffMemberNzOnGet(staffMember)
    case Country.Australia:
      if (payrollSystem === PayrollSystem.XeroAu) {
        return transformStaffMemberXeroAuOnGet(staffMember)
      } else {
        return transformStaffMemberAuOnGet(staffMember)
      }
    default:
      return transformBaseStaffMemberOnGet(staffMember)
  }
}

// Function to apply transformations to StaffMember object on get
export const transformStaffMemberOnSave = (
  payrollSystem: PayrollSystem | null,
  staffMember: StaffMember,
  country: Country
): object => {
  let resultStaffMember: object

  switch (country) {
    case Country.NewZealand:
      resultStaffMember = transformStaffMemberNzOnSave(staffMember)
      break
    case Country.Australia:
      if (payrollSystem === PayrollSystem.XeroAu) {
        resultStaffMember = transformStaffMemberXeroAuOnSave(staffMember)
      } else {
        resultStaffMember = transformStaffMemberAuOnSave(staffMember)
      }
      break
    default:
      resultStaffMember = transformBaseStaffMemberOnSave(staffMember)
      break
  }
  return resultStaffMember
}
